import { useState } from 'react';
import { Mail, Phone, Linkedin, MessageSquare, StickyNote, Plus, Send, ChevronDown } from 'lucide-react';
import type { ContactActionLog, ContactNote, ContactActionType, ChannelOutcome } from '../../types';

type TimelineEntry =
  | { kind: 'action'; data: ContactActionLog }
  | { kind: 'note'; data: ContactNote };

interface Props {
  actions: ContactActionLog[];
  notes: ContactNote[];
  onLogAction: (action: { action_type: ContactActionType; channel_outcome: ChannelOutcome; notes: string }) => Promise<void>;
  onAddNote: (text: string) => Promise<void>;
  loading: boolean;
}

const ACTION_TYPE_CONFIG: Record<string, { icon: typeof Mail; label: string; color: string }> = {
  email_sent: { icon: Mail, label: 'Email Sent', color: 'text-app-focus bg-app-focus/10' },
  linkedin_sent: { icon: Linkedin, label: 'LinkedIn Sent', color: 'text-app-focus bg-app-focus/10' },
  call_made: { icon: Phone, label: 'Call Made', color: 'text-app-positive bg-app-positive/10' },
  other: { icon: MessageSquare, label: 'Other', color: 'text-app-muted bg-app-canvas' },
};

const OUTCOME_LABELS: Record<string, string> = {
  responded: 'Responded',
  not_responded: 'No Response',
  answered: 'Answered',
  not_answered: 'Not Answered',
  voicemail: 'Voicemail',
  left_message: 'Left Message',
  n_a: 'N/A',
};

const EMAIL_OUTCOMES: { value: ChannelOutcome; label: string }[] = [
  { value: 'responded', label: 'Responded' },
  { value: 'not_responded', label: 'No Response' },
];

const CALL_OUTCOMES: { value: ChannelOutcome; label: string }[] = [
  { value: 'answered', label: 'Answered' },
  { value: 'not_answered', label: 'Not Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'left_message', label: 'Left Message' },
];

function getOutcomeOptions(actionType: ContactActionType): { value: ChannelOutcome; label: string }[] {
  switch (actionType) {
    case 'email_sent':
    case 'linkedin_sent':
      return EMAIL_OUTCOMES;
    case 'call_made':
      return CALL_OUTCOMES;
    default:
      return [{ value: 'n_a', label: 'N/A' }];
  }
}

export default function ContactActivityTimeline({ actions, notes, onLogAction, onAddNote, loading }: Props) {
  const [showActionForm, setShowActionForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [actionType, setActionType] = useState<ContactActionType>('email_sent');
  const [outcome, setOutcome] = useState<ChannelOutcome>('not_responded');
  const [actionNotes, setActionNotes] = useState('');
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const timeline: TimelineEntry[] = [
    ...actions.map(a => ({ kind: 'action' as const, data: a })),
    ...notes.map(n => ({ kind: 'note' as const, data: n })),
  ].sort((a, b) => new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime());

  const handleActionTypeChange = (type: ContactActionType) => {
    setActionType(type);
    const opts = getOutcomeOptions(type);
    setOutcome(opts[0]?.value ?? 'n_a');
  };

  const handleSubmitAction = async () => {
    if (submitting) return;
    setSubmitting(true);
    await onLogAction({ action_type: actionType, channel_outcome: outcome, notes: actionNotes });
    setShowActionForm(false);
    setActionNotes('');
    setActionType('email_sent');
    setOutcome('not_responded');
    setSubmitting(false);
  };

  const handleSubmitNote = async () => {
    if (submitting || !noteText.trim()) return;
    setSubmitting(true);
    await onAddNote(noteText.trim());
    setShowNoteForm(false);
    setNoteText('');
    setSubmitting(false);
  };

  const outcomeOptions = getOutcomeOptions(actionType);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setShowActionForm(!showActionForm); setShowNoteForm(false); }}
          className="flex items-center gap-1.5 px-3 py-2 bg-forest-600 text-white text-xs font-semibold rounded-lg hover:bg-forest-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Log Action
        </button>
        <button
          onClick={() => { setShowNoteForm(!showNoteForm); setShowActionForm(false); }}
          className="flex items-center gap-1.5 px-3 py-2 bg-app-surface border border-app-line text-app-ink text-xs font-semibold rounded-lg hover:bg-app-canvas transition-colors"
        >
          <StickyNote className="w-3.5 h-3.5" />
          Add Note
        </button>
      </div>

      {showActionForm && (
        <div className="bg-app-surface border border-app-line rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-app-muted uppercase tracking-wider mb-1">Action Type</label>
              <div className="relative">
                <select
                  value={actionType}
                  onChange={e => handleActionTypeChange(e.target.value as ContactActionType)}
                  className="w-full px-3 py-2 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink appearance-none focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400"
                >
                  <option value="email_sent">Email Sent</option>
                  <option value="linkedin_sent">LinkedIn Message Sent</option>
                  <option value="call_made">Call Made</option>
                  <option value="other">Other</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-app-muted uppercase tracking-wider mb-1">Outcome</label>
              <div className="relative">
                <select
                  value={outcome}
                  onChange={e => setOutcome(e.target.value as ChannelOutcome)}
                  className="w-full px-3 py-2 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink appearance-none focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400"
                >
                  {outcomeOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-app-muted uppercase tracking-wider mb-1">Notes (optional)</label>
            <textarea
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              rows={2}
              placeholder="Add any context about this action..."
              className="w-full px-3 py-2 border border-app-line rounded-lg text-sm text-app-ink placeholder:text-app-muted focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400 resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowActionForm(false)} className="px-3 py-1.5 text-xs text-app-muted hover:text-app-ink">Cancel</button>
            <button
              onClick={handleSubmitAction}
              disabled={submitting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-forest-600 text-white text-xs font-semibold rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
            >
              <Send className="w-3 h-3" />
              {submitting ? 'Logging...' : 'Log Action'}
            </button>
          </div>
        </div>
      )}

      {showNoteForm && (
        <div className="bg-app-warning/10 border border-app-warning rounded-lg p-4 space-y-3">
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={3}
            placeholder="Write a note for your team..."
            className="w-full px-3 py-2 bg-app-surface border border-app-warning rounded-lg text-sm text-app-ink placeholder:text-app-muted focus:outline-none focus:ring-2 focus:ring-app-warning/20 focus:border-app-warning resize-none"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNoteForm(false)} className="px-3 py-1.5 text-xs text-app-muted hover:text-app-ink">Cancel</button>
            <button
              onClick={handleSubmitNote}
              disabled={submitting || !noteText.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-app-warning text-white text-xs font-semibold rounded-lg hover:bg-app-warning disabled:opacity-50 transition-colors"
            >
              <Send className="w-3 h-3" />
              {submitting ? 'Posting...' : 'Post Note'}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-4">
          <div className="w-5 h-5 border-2 border-forest-300 border-t-forest-600 rounded-full animate-spin mx-auto" />
        </div>
      )}

      {!loading && timeline.length === 0 && (
        <div className="text-center py-8">
          <MessageSquare className="w-8 h-8 text-app-line-strong mx-auto mb-2" />
          <p className="text-xs text-app-muted">No activity logged yet</p>
        </div>
      )}

      <div className="space-y-0">
        {timeline.map(entry => {
          if (entry.kind === 'action') {
            const a = entry.data;
            const config = ACTION_TYPE_CONFIG[a.action_type] || ACTION_TYPE_CONFIG.other;
            const Icon = config.icon;
            const outcomeLabel = OUTCOME_LABELS[a.channel_outcome] || a.channel_outcome;

            return (
              <div key={`a-${a.id}`} className="flex gap-3 py-3 border-b border-app-line last:border-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-app-ink">{config.label}</span>
                    {a.channel_outcome !== 'n_a' && (
                      <span className="px-1.5 py-0.5 bg-app-canvas text-[10px] font-medium text-app-muted rounded">
                        {outcomeLabel}
                      </span>
                    )}
                  </div>
                  {a.notes && <p className="text-sm text-app-muted mt-0.5 leading-relaxed">{a.notes}</p>}
                  <p className="text-[11px] text-app-muted mt-1">
                    {a.user_name || a.user_email || 'Team member'} &middot; {formatTimestamp(a.created_at)}
                  </p>
                </div>
              </div>
            );
          }

          const n = entry.data;
          return (
            <div key={`n-${n.id}`} className="flex gap-3 py-3 border-b border-app-line last:border-0">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-app-warning/10 text-app-warning">
                <StickyNote className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-app-ink leading-relaxed">{n.note_text}</p>
                <p className="text-[11px] text-app-muted mt-1">
                  {n.user_name || n.user_email || 'Team member'} &middot; {formatTimestamp(n.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
